import { format, formatDistanceToNowStrict, isValid, parseISO } from 'date-fns';

export function formatDateTime(value: string | Date | null | undefined, fallback = '—'): string {
  if (!value) return fallback;
  const date = value instanceof Date ? value : parseISO(value);
  if (!isValid(date)) return fallback;
  return format(date, 'MMM d, yyyy HH:mm');
}

export function formatDate(value: string | Date | null | undefined, fallback = '—'): string {
  if (!value) return fallback;
  const date = value instanceof Date ? value : parseISO(value);
  if (!isValid(date)) return fallback;
  return format(date, 'MMM d, yyyy');
}

export function formatRelative(value: string | Date | null | undefined, fallback = '—'): string {
  if (!value) return fallback;
  const date = value instanceof Date ? value : parseISO(value);
  if (!isValid(date)) return fallback;
  try {
    return `${formatDistanceToNowStrict(date, { addSuffix: true })}`;
  } catch {
    return fallback;
  }
}

export function formatHoursCompact(hours: number | null | undefined): string {
  if (hours === null || hours === undefined || Number.isNaN(hours)) return '—';
  if (Math.abs(hours) < 1) {
    const minutes = Math.round(hours * 60);
    return `${minutes}m`;
  }
  if (Math.abs(hours) < 48) {
    return `${hours.toFixed(1)}h`;
  }
  const days = hours / 24;
  return `${days.toFixed(1)}d`;
}

/**
 * Format a byte count into a human-readable string. Accepts the string form
 * (TypeORM bigint columns deserialize as strings) or a number.
 *   formatBytes(245)        → "245 B"
 *   formatBytes(2048)       → "2.0 KB"
 *   formatBytes(2_500_000)  → "2.4 MB"
 */
export function formatBytes(value: string | number | null | undefined, fallback = '—'): string {
  if (value === null || value === undefined || value === '') return fallback;
  const bytes = typeof value === 'string' ? Number(value) : value;
  if (!Number.isFinite(bytes) || bytes < 0) return fallback;
  if (bytes === 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  let i = 0;
  let n = bytes;
  while (n >= 1024 && i < units.length - 1) {
    n /= 1024;
    i += 1;
  }
  return `${n.toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
}

export function initials(name: string | null | undefined): string {
  if (!name) return '?';
  return name
    .split(' ')
    .map((part) => part.charAt(0).toUpperCase())
    .slice(0, 2)
    .join('');
}
